import { z } from 'zod';

/** TI, Manutenção e Projetos (backlog M1, M2, PJ1, PJ2 e G9; 0343). */

export const IT_STOCK_MOVEMENT_KINDS = ['in', 'out_repair', 'adjust'] as const;
export const IT_PROJECT_STATUSES = [
  'planned',
  'in_progress',
  'on_hold',
  'done',
  'cancelled',
] as const;
export const IT_CHANGE_KINDS = ['standard', 'normal', 'emergency'] as const;
export const IT_CHANGE_CATEGORIES = [
  'network',
  'firewall',
  'server',
  'application',
  'workstation',
  'other',
] as const;
export const IT_CHANGE_RISKS = ['low', 'medium', 'high'] as const;
export const IT_CHANGE_STATUSES = [
  'draft',
  'awaiting_approval',
  'approved',
  'rejected',
  'done',
  'failed',
  'rolled_back',
  'cancelled',
] as const;
export const IT_CHANGE_CLOSINGS = ['done', 'failed', 'rolled_back', 'cancelled'] as const;

const id = z.uuid().nullable().optional();
const day = z.iso.date();
const text = (min: number, max: number) => z.string().trim().min(min).max(max);
const amount = z
  .string()
  .trim()
  .regex(/^\d{1,15}(\.\d{1,4})?$/, 'Use números com ponto (ex.: 1500.00)');
/** Quantidade com até 3 casas; o ajuste de inventário aceita negativo. */
const quantity = z
  .string()
  .trim()
  .regex(/^-?\d{1,9}(\.\d{1,3})?$/, 'Use números com ponto (ex.: 2 ou 1.5)')
  .refine((v) => !/^-?0+(\.0+)?$/.test(v), 'A quantidade não pode ser zero');
const code = z
  .string()
  .trim()
  .regex(/^[A-Z0-9][A-Z0-9_.-]{1,29}$/, 'Use de 2 a 30 letras maiúsculas, números, ., - ou _');

export const itStockItemInput = z.object({
  legalEntityId: z.uuid(),
  code,
  name: text(2, 200),
  unit: text(1, 10).default('un'),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Escolha a moeda'),
  minQuantity: z
    .string()
    .trim()
    .regex(/^\d{1,9}(\.\d{1,3})?$/, 'Use números com ponto')
    .default('0'),
  isActive: z.boolean().default(true),
});

export const itStockMovementInput = z
  .object({
    kind: z.enum(IT_STOCK_MOVEMENT_KINDS),
    quantity,
    unitAmount: amount.optional(),
    movedOn: day,
    repairId: id,
    documentRef: text(1, 120).optional(),
    note: text(3, 1000).optional(),
  })
  .refine((v) => v.kind === 'adjust' || !v.quantity.startsWith('-'), {
    path: ['quantity'],
    message: 'Só o ajuste de inventário aceita quantidade negativa',
  })
  .refine((v) => v.kind !== 'in' || !!v.unitAmount, {
    path: ['unitAmount'],
    message: 'Informe o custo unitário da entrada',
  })
  .refine((v) => v.kind !== 'out_repair' || !!v.repairId, {
    path: ['repairId'],
    message: 'Escolha o reparo que recebe a peça',
  })
  .refine((v) => v.kind !== 'adjust' || !!v.note, {
    path: ['note'],
    message: 'Explique o ajuste',
  });

export const itMaintenancePlanInput = z.object({
  legalEntityId: z.uuid(),
  name: text(3, 200),
  assetClassId: z.uuid(),
  frequencyDays: z.number().int().min(7).max(1095),
  checklist: text(3, 4000),
  isActive: z.boolean().default(true),
});

export const itMaintenanceRunInput = z
  .object({
    assetId: z.uuid(),
    doneOn: day,
    result: z.enum(['ok', 'issues']),
    note: text(3, 2000).optional(),
  })
  .refine((v) => v.result === 'ok' || !!v.note, {
    path: ['note'],
    message: 'Conte o que foi encontrado',
  });

export const itProjectInput = z
  .object({
    legalEntityId: z.uuid(),
    name: text(3, 200),
    description: text(3, 4000).optional(),
    sponsorUserId: id,
    managerUserId: z.uuid(),
    status: z.enum(IT_PROJECT_STATUSES).default('planned'),
    startOn: day.nullable().optional(),
    dueOn: day.nullable().optional(),
    doneOn: day.nullable().optional(),
    progress: z.number().int().min(0).max(100).default(0),
    budgetAmount: amount.nullable().optional(),
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .nullable()
      .optional(),
    statusNote: text(3, 2000).optional(),
  })
  .refine((v) => !v.budgetAmount || !!v.currency, {
    path: ['currency'],
    message: 'Escolha a moeda do orçamento',
  });

export const itChangeInput = z
  .object({
    legalEntityId: z.uuid(),
    title: text(3, 200),
    description: text(3, 4000),
    kind: z.enum(IT_CHANGE_KINDS),
    category: z.enum(IT_CHANGE_CATEGORIES),
    risk: z.enum(IT_CHANGE_RISKS),
    systemId: id,
    assetId: id,
    projectId: id,
    costCenterId: z.uuid(),
    executorUserId: z.uuid(),
    plannedStart: z.iso.datetime({ offset: true }),
    plannedEnd: z.iso.datetime({ offset: true }),
    rollbackPlan: text(3, 4000),
  })
  .refine((v) => v.kind !== 'standard' || v.risk === 'low', {
    path: ['risk'],
    message: 'Mudança padrão é só de risco baixo',
  })
  .refine((v) => new Date(v.plannedEnd) > new Date(v.plannedStart), {
    path: ['plannedEnd'],
    message: 'O fim precisa ser depois do início',
  });

export const itChangeCloseInput = z.object({
  status: z.enum(IT_CHANGE_CLOSINGS),
  resultNote: text(3, 4000),
});
